import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Item } from '@siat-fe/shared';
import { pipe, switchMap } from 'rxjs';
import { RegioniProvinceComuniService } from '../http/regioni-province-comuni.service';

/**
 * State type.
 */
type RegioniProvinceComuniState = {
  regioniItems: Item[];
  provinceItems: Item[];
  comuniItems: Item[];
  hasError: boolean;
};

/**
 * Initial state.
 */
const initialState: RegioniProvinceComuniState = {
  regioniItems: [],
  provinceItems: [],
  comuniItems: [],
  hasError: false,
};

export const RegioniProvinceComuniStore = signalStore(
  withState<RegioniProvinceComuniState>(initialState),
  withMethods(store => {
    /**
     * Set error.
     */
    const _setError = (hasError: boolean, _message?: string): void => {
      patchState(store, { hasError });
    };

    /**
     * Set regioni items.
     */
    const _setRegioniItems = (regioniResponse: { nome: string }[]): void => {
      patchState(store, {
        regioniItems: regioniResponse.map(regione => ({
          id: regione.nome,
          name: regione.nome,
        })),
        hasError: false,
      });
    };

    /**
     * Set province items.
     */
    const _setProvinceItems = (provinceResponse: { nome: string }[]): void => {
      patchState(store, {
        provinceItems: provinceResponse.map(provincia => ({
          id: provincia.nome,
          name: provincia.nome,
        })),
        hasError: false,
      });
    };
    /**
     * Set comuni items.
     */
    const _setComuniItems = (comuniResponse: { nome: string }[]): void => {
      patchState(store, {
        comuniItems: comuniResponse.map(comune => ({
          id: comune.nome,
          name: comune.nome,
        })),
        hasError: false,
      });
    };

    return {
      _setError,
      _setRegioniItems,
      _setProvinceItems,
      _setComuniItems,
    };
  }),
  withMethods(
    (
      store,
      regioniProvinceComuniService = inject(RegioniProvinceComuniService)
    ) => {
      /**
       * Fetch get all regioni.
       */
      const fetchGetAllRegioni = rxMethod<void>(
        pipe(
          switchMap(() =>
            regioniProvinceComuniService.getAllRegioni().pipe(
              tapResponse({
                next: (response: { nome: string }[]) =>
                  store._setRegioniItems(response),
                error: () => store._setError(true),
              })
            )
          )
        )
      );
      /**
       * Fetch get all province.
       */
      const fetchGetAllProvince = rxMethod<string>(
        pipe(
          switchMap((regione: string) =>
            regioniProvinceComuniService.getAllProvince(regione).pipe(
              tapResponse({
                next: (response: { nome: string }[]) =>
                  store._setProvinceItems(response),
                error: () => store._setError(true),
              })
            )
          )
        )
      );
      /**
       * Fetch get all comuni.
       */
      const fetchGetAllComuni = rxMethod<string>(
        pipe(
          switchMap((provincia: string) =>
            regioniProvinceComuniService.getAllComuni(provincia).pipe(
              tapResponse({
                next: (response: { nome: string }[]) =>
                  store._setComuniItems(response),
                error: () => store._setError(true),
              })
            )
          )
        )
      );
      return {
        fetchGetAllRegioni,
        fetchGetAllProvince,
        fetchGetAllComuni,
      };
    }
  )
);
